import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Phone, Mail, Instagram, MapPin, Loader2 } from "lucide-react";
import { CONTACT, SERVICES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["Under ₹25K / month", "₹25K – ₹75K / month", "₹75K – ₹2L / month", "₹2L+ / month"];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-all duration-300 focus:border-[#A3E635]/60 focus:shadow-[0_0_24px_rgba(163,230,53,0.12)]";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", budget: "", message: "" });
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleService = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, services });
      toast.success("Enquiry received. Our growth team will reach out within 24 hours.");
      setForm({ name: "", company: "", email: "", phone: "", budget: "", message: "" });
      setServices([]);
    } catch {
      toast.error("Something went wrong. Please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative border-t border-white/10 bg-[#0d1013] overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-96 w-[42rem] rounded-full bg-[#A3E635]/10 blur-[120px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <div>
          <p className="font-mono2 text-xs sm:text-sm tracking-[0.25em] text-[#A3E635] uppercase mb-4">
            // Your move
          </p>
          <h2 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[1.0] mb-6">
            Ready to build your <span className="text-[#A3E635]">brand IP?</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-400 leading-relaxed mb-10 max-w-md">
            Partner with Bengaluru's brand engineering & performance marketing crew. Get a free custom growth audit — no fluff, just a plan.
          </p>

          <div className="flex flex-col gap-5">
            <a data-testid="contact-phone-link" href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 text-slate-300 hover:text-[#A3E635] transition-colors duration-300">
              <span className="h-11 w-11 rounded-full border border-white/10 bg-black/50 flex items-center justify-center"><Phone size={17} /></span>
              <span className="text-sm font-medium">{CONTACT.phone}</span>
            </a>
            <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 text-slate-300 hover:text-[#A3E635] transition-colors duration-300">
              <span className="h-11 w-11 rounded-full border border-white/10 bg-black/50 flex items-center justify-center"><Mail size={17} /></span>
              <span className="text-sm font-medium">{CONTACT.email}</span>
            </a>
            <a data-testid="contact-instagram-link" href={CONTACT.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-4 text-slate-300 hover:text-[#A3E635] transition-colors duration-300">
              <span className="h-11 w-11 rounded-full border border-white/10 bg-black/50 flex items-center justify-center"><Instagram size={17} /></span>
              <span className="text-sm font-medium">@brandedbyknowfinity</span>
            </a>
            <div className="flex items-start gap-4 text-slate-400">
              <span className="h-11 w-11 shrink-0 rounded-full border border-white/10 bg-black/50 flex items-center justify-center"><MapPin size={17} /></span>
              <span className="text-sm leading-relaxed pt-2.5">{CONTACT.address}</span>
            </div>
          </div>
        </div>

        <form
          data-testid="enquiry-form"
          onSubmit={submit}
          className="rounded-2xl border border-white/10 bg-[#121519]/80 backdrop-blur-xl p-7 sm:p-9 flex flex-col gap-5 glow-lime"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <input data-testid="enquiry-form-input-name" required placeholder="Full Name *" value={form.name} onChange={set("name")} className={inputCls} />
            <input data-testid="enquiry-form-input-company" placeholder="Company / Brand" value={form.company} onChange={set("company")} className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <input data-testid="enquiry-form-input-email" required type="email" placeholder="Email Address *" value={form.email} onChange={set("email")} className={inputCls} />
            <input data-testid="enquiry-form-input-phone" required placeholder="Phone / WhatsApp *" value={form.phone} onChange={set("phone")} className={inputCls} />
          </div>

          <select data-testid="enquiry-form-input-budget" value={form.budget} onChange={set("budget")} className={`${inputCls} appearance-none ${form.budget ? "" : "text-slate-500"}`}>
            <option value="" disabled>Estimated Monthly Budget</option>
            {BUDGETS.map((b) => <option key={b} value={b} className="bg-[#121519] text-white">{b}</option>)}
          </select>

          <div>
            <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-3">Services required</p>
            <div className="flex flex-wrap gap-2">
              {SERVICES.map((s) => (
                <button
                  type="button"
                  key={s.num}
                  data-testid={`enquiry-service-chip-${s.num}`}
                  onClick={() => toggleService(s.title)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all duration-300 ${
                    services.includes(s.title)
                      ? "border-[#A3E635] bg-[#A3E635]/15 text-[#A3E635]"
                      : "border-white/10 bg-black/40 text-slate-400 hover:border-[#A3E635]/40"
                  }`}
                >
                  {s.title}
                </button>
              ))}
            </div>
          </div>

          <textarea
            data-testid="enquiry-form-input-message"
            placeholder="Project goal / message"
            rows={4}
            value={form.message}
            onChange={set("message")}
            className={`${inputCls} resize-none`}
          />

          <button
            data-testid="enquiry-form-submit-button"
            type="submit"
            disabled={loading}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#A3E635] px-7 py-4 font-mono2 text-sm font-bold tracking-[0.15em] uppercase text-black transition-all duration-300 hover:bg-[#84CC16] hover:shadow-[0_0_40px_rgba(163,230,53,0.45)] disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
            {loading ? "Sending..." : "Claim My Growth Audit"}
          </button>
        </form>
      </div>
    </section>
  );
};
